import qrcode from 'qrcode-generator';
import { BookOpen, ChevronDown, Mail } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { CURRENT_RELEASE, RELEASES } from '../../about/changelog';
import { endpoints } from '../../services/api';
import { buttonClass, ghostButtonClass, Section } from './controls';

const fmtDate = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
/** A release date (YYYY-MM-DD) as a local calendar day. */
const releaseDay = (d: string) => fmtDate.format(new Date(`${d}T12:00:00`));

/** How many releases show before "Show older". */
const RECENT = 3;

/**
 * Settings → About & help: the how-to guide (a link, and a QR code for TVs), support, the
 * version and release date, the build on the server, and what changed in each release.
 */
export function AboutTab() {
  const [build, setBuild] = useState<{ version: string; builtAt: string | null; env: string } | null>(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    let live = true;
    endpoints
      .health()
      .then((h) => live && setBuild(h))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const guideUrl = `${window.location.origin}/help`;
  const qr = useMemo(() => {
    const q = qrcode(0, 'M');
    q.addData(guideUrl);
    q.make();
    return q.createDataURL(4, 2);
  }, [guideUrl]);
  const releases = showAll ? RELEASES : RELEASES.slice(0, RECENT);

  return (
    <>
      <Section title="How-to guide" description="Step-by-step help with pictures: setting up a screen, adding photos, widgets, layouts, screens and sharing.">
        <div className="flex flex-wrap items-center gap-5">
          <img src={qr} alt="QR code for the how-to guide" className="h-32 w-32 rounded-lg bg-white p-1" />
          <div className="space-y-3">
            <a className={buttonClass} href="/help" target="_blank" rel="noopener">
              <BookOpen className="h-4 w-4" aria-hidden /> Open the guide
            </a>
            <p className="muted text-sm">
              On a TV, scan the code with your phone, or go to <span className="font-medium text-[var(--fg)]">{guideUrl.replace(/^https?:\/\//, '')}</span>.
            </p>
            <a className={ghostButtonClass} href="mailto:support@kcl.cloud">
              <Mail className="h-4 w-4" aria-hidden /> support@kcl.cloud
            </a>
          </div>
        </div>
      </Section>

      <Section title="About KCL Canvas">
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-sm">
          <dt className="muted">Version</dt>
          <dd className="font-medium">{CURRENT_RELEASE.version}</dd>
          <dt className="muted">Released</dt>
          <dd>{releaseDay(CURRENT_RELEASE.date)}</dd>
          <dt className="muted">Build</dt>
          <dd>
            {build ? (
              <>
                {build.version}
                {build.builtAt ? ` · ${fmtDate.format(new Date(build.builtAt))}` : ''}
                {build.env !== 'production' ? ` · ${build.env}` : ''}
              </>
            ) : (
              '…'
            )}
          </dd>
          <dt className="muted">Publisher</dt>
          <dd>
            <a href="https://kcltd.es" target="_blank" rel="noopener" className="hover:underline">
              Kirkpatrick Consult Limited
            </a>
          </dd>
        </dl>
        <p className="muted flex flex-wrap gap-x-3 gap-y-1 text-sm">
          <a href="https://kcl.cloud/canvas/" target="_blank" rel="noopener" className="hover:underline">
            kcl.cloud/canvas
          </a>
          <a href="/privacy" className="hover:underline">
            Privacy
          </a>
          <a href="/terms" className="hover:underline">
            Terms
          </a>
        </p>
      </Section>

      <Section title="What’s new">
        <ol className="space-y-5" aria-label="Changelog">
          {releases.map((r) => (
            <li key={r.version}>
              <h4 className="flex flex-wrap items-baseline gap-x-2 text-sm">
                <span className="font-semibold">
                  {r.version} – {r.title}
                </span>
                <span className="muted text-xs">{releaseDay(r.date)}</span>
              </h4>
              <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm">
                {r.changes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
        {RELEASES.length > RECENT && (
          <button type="button" className={ghostButtonClass} onClick={() => setShowAll(!showAll)}>
            <ChevronDown className={`h-4 w-4 transition ${showAll ? 'rotate-180' : ''}`} aria-hidden /> {showAll ? 'Show fewer' : 'Show older releases'}
          </button>
        )}
      </Section>
    </>
  );
}
